import { useQuery } from "@tanstack/react-query";
import { CompanyUserWithProfile, usersApi } from "@/app/lib/api/users";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { SupabaseUser } from "@/app/lib/supabase/types";

const COMPANY_USERS_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const COMPANY_USERS_GC_TIME = 30 * 60 * 1000; // 30 minutes

export const companyUsersQueryKeys = {
  all: ["company-users"] as const,
  byCompany: (companyId: string, userRole: string) =>
    [...companyUsersQueryKeys.all, companyId, userRole] as const,
  userDetail: (userId: string) =>
    [...companyUsersQueryKeys.all, "user", userId] as const,
};

export function useCompanyUsers() {
  const { selectedCompanyUser } = useAppStore();

  return useQuery<CompanyUserWithProfile[], Error>({
    queryKey: companyUsersQueryKeys.byCompany(
      selectedCompanyUser?.company_id || "",
      selectedCompanyUser?.role || ""
    ),
    queryFn: () => {
      if (!selectedCompanyUser) throw new Error("No company selected");
      if (selectedCompanyUser.role !== 'admin' && selectedCompanyUser.role !== 'manager') {
        return Promise.resolve([]); // Return empty array for non-authorized users
      }
      return usersApi.getCompanyUsers(selectedCompanyUser.company_id, selectedCompanyUser.role);
    },
    enabled: !!selectedCompanyUser &&
      (selectedCompanyUser.role === 'admin' || selectedCompanyUser.role === 'manager'),
    staleTime: COMPANY_USERS_STALE_TIME,
    gcTime: COMPANY_USERS_GC_TIME,
  });
}

// Alternative hook that accepts companyId and role as parameters
export function useCompanyUsersByParams(companyId: string | null, userRole: string | null) {
  return useQuery<CompanyUserWithProfile[], Error>({
    queryKey: companyUsersQueryKeys.byCompany(companyId || "", userRole || ""),
    queryFn: () => {
      if (!companyId || !userRole) throw new Error("Company ID and user role required");
      if (userRole !== 'admin' && userRole !== 'manager') {
        return Promise.resolve([]); // Return empty array for non-authorized users
      }
      return usersApi.getCompanyUsers(companyId, userRole);
    },
    enabled: !!companyId && !!userRole &&
      (userRole === 'admin' || userRole === 'manager'),
    staleTime: COMPANY_USERS_STALE_TIME,
    gcTime: COMPANY_USERS_GC_TIME,
  });
}

// Hook to fetch a single user by ID
export function useUserById(userId: string | null | undefined) {
  return useQuery<SupabaseUser | null, Error>({
    queryKey: companyUsersQueryKeys.userDetail(userId || ""),
    queryFn: async () => {
      if (!userId) return null;
      return usersApi.getUserById(userId);
    },
    enabled: !!userId,
    staleTime: COMPANY_USERS_STALE_TIME,
    gcTime: COMPANY_USERS_GC_TIME,
  });
}